import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';

import { throwError, Observable, of, Subject } from 'rxjs';
import { tap, catchError, take, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Store } from '@ngrx/store';

import { selectApiState } from '../store/selectors/api.selectors';
import { ApiResponse } from 'src/app/models/api';
import { Action_Api_Online, Action_Api_Offline } from '../store/actions/api.actions';
import { ApiError } from '../models/rootState.model';
import { UtilService } from './util.service';
import { ErrorService } from './error.service';
import { Router } from '@angular/router';
import { Auth, signOut } from '@angular/fire/auth';
import { from } from 'rxjs';

import { CacheApi } from 'src/app/features/cache/cache-api';
import { CacheGroup } from 'src/app/features/cache/cache-table';
import { ServerTimeService } from './server-time.service';

export class ServerResponseError {
  error: {
    code: number;
    message: string;
    name: string;
  };
  extraInfo?: any;
  payload: any;
  status: number;
  stime: string;
  version: {
    env: string;
    version: string;
  }

  constructor(o: ServerResponseError) {
    Object.assign(this, o);
  }


  get message() {
    return this.error.message;
  }

  isNamed(name: string) {
    return this.error?.name == name;
  }

  sentExtra<T = any>(key: string): T {
    if (this.extraInfo) {
      return this.extraInfo[key] as T;
    }
    return null;
  }


}
export class ApiResponseError {
  loaderClosed: boolean;
  messageDisplayed: boolean;
  errorMessage: string;
  track: string;
  code: number;
  extra: any;
  apiResponse: ServerResponseError;

  constructor(o: Partial<ApiResponseError>) {
    Object.assign(this, o);
    if (!(this.apiResponse instanceof ServerResponseError)) {
      this.apiResponse = new ServerResponseError(o.apiResponse);
    }
  }

  get messageFriendly() {
    return this.apiResponse?.message;
  }


  isNamed(name: string) {
    return this.apiResponse?.isNamed(name);
  }

  sentExtra<T = any>(key: string): T {
    return this.apiResponse?.sentExtra<T>(key);
  }

}


export type ApiOptionsReturnType = 'cached' | 'api' | 'cached-or-api' | 'cached-and-api';

export interface ApiOptions {
  silent?: boolean;
  silentIfCode?: number[];
  silentIfNamed?: string[];
  acceptCache?: string;
  cacheGroup?: CacheGroup;
  returnType?: ApiOptionsReturnType;
  invalidateOnSuccess?: CacheGroup[];
  justInvalidate?: boolean;
}

@Injectable()
export class ApiService {

  private _apiPath: string;

  private apiState$ = this.store.select(selectApiState);

  private hasConnection = false;

  private cache: CacheApi<ApiResponse<any>> = new CacheApi();


  private cleanCachetimer;


  constructor(
    private http: HttpClient,
    private util: UtilService,
    private store: Store<any>,
    private router: Router,
    private af: Auth,
    private errorService: ErrorService,
    private serverTime: ServerTimeService
    // private cache: AppCacheService
  ) {
    this.hasConnection = true;
    this._apiPath = ['development', 'local'].includes(environment.environment)
      ? 'api'
      : `${environment.api.host}/api`;

    this.apiState$.subscribe(state => this.hasConnection = state.online);

    this.cleanCachetimer = setInterval(() => {
      this.cleanInvalidCache();
    }, 60000 * 10);
    this.cleanInvalidCache();
  }

  private cleanInvalidCache() {
    this.cache.cleanStore();
  }

  private path(resource: string): string {
    if (resource.startsWith('http://') || resource.startsWith('https://')) {
      return resource;
    }
    const path = `${this._apiPath}/${resource}`;
    return path;
  }

  makeParams(parameters?: { [index: string]: string }): { params: HttpParams } {
    let params = new HttpParams();
    if (parameters) {
      const keys = Object.keys(parameters);
      if (keys.length > 0) {
        params = new HttpParams();
        keys.forEach(k => {
          params = params.append(k, parameters[k]);
        });
      }
    }
    return { params };
  }


  get<T>(resource: string, parameters?: { [index: string]: string }, options?: ApiOptions): Observable<ApiResponse<T>> {
    const params = this.makeParams(parameters);
    const cacheKey = resource + (parameters ? '|' + JSON.stringify(parameters) : '');

    let baseResult: Observable<ApiResponse<T>>;

    if (!options) {
      options = { returnType: 'api' };
    }

    if (!options.returnType) {
      if (options.acceptCache) {
        options.returnType = 'cached-or-api';
      } else {
        options.returnType = 'api';
      }
    }

    if (options.justInvalidate == true) {
      return from(this.cache.delete(cacheKey));
    }

    const fromApi = this.http.get<ApiResponse<T>>(this.path(resource), params)
      .pipe(
        tap(() => this.handleServeStatus(false)),
        tap(res => this.setServerTime(res?.stime ?? '')),
        tap((value) => {
          if (options && options.returnType != 'api') {
            const cacheGroup = options?.cacheGroup ?? 'default';
            this.cache.set(cacheKey, value, { expires: options?.acceptCache ?? '30m', group: cacheGroup }).then();
          }

          if (options && options.invalidateOnSuccess) {
            this.cache.invalidateGroups(options.invalidateOnSuccess);
          }
        }),
        take(1),
        catchError(err => {
          if (!environment.production) {
            console.error({ place: 'API', method: 'GET', path: this.path(resource), params, error: err });
          }
          return throwError(this.handleError(err, resource, 'GET', null, options));
        })
      );

    const fromCache = from(this.cache.get(cacheKey, options.acceptCache));

    let resultEmitter: Subject<ApiResponse<T>>;

    if (options.returnType == 'api') {
      baseResult = fromApi;
    } else if (options.returnType == 'cached') {
      baseResult = fromCache;
    } else if (options.returnType == 'cached-and-api') {
      resultEmitter = new Subject();
      baseResult = resultEmitter.asObservable();
      fromCache.subscribe(
        dataFromCache => {
          if (dataFromCache) {
            resultEmitter.next(dataFromCache);
          }

          fromApi.subscribe(_dataFromApi => {
            resultEmitter.next(_dataFromApi);
            resultEmitter.complete();
          }, error => {
            resultEmitter.error(error);
          });

        },
        error => {
          resultEmitter.error(error);
        });


    } else if (options.returnType == 'cached-or-api') {
      baseResult = fromCache.pipe(
        switchMap((dataFromCache) => {
          if (dataFromCache) {
            return of(dataFromCache);
          }
          return fromApi;
        })
      );
    }

    return baseResult;
  }


  put<T>(resource: string, data: any, parameters?: { [index: string]: string }, options?: ApiOptions): Observable<ApiResponse<T>> {
    const params = this.makeParams(parameters);
    return this.http.put<ApiResponse<T>>(this.path(resource), data, params).pipe(
      tap(() => this.handleServeStatus(false)),
      tap(res => this.setServerTime(res?.stime ?? '')),
      tap((value) => {

        if (options && options.invalidateOnSuccess) {
          this.cache.invalidateGroups(options.invalidateOnSuccess);
        }


      }),
      take(1),
      catchError(err => {
        if (!environment.production) {
          console.error({ place: 'API', method: 'PUT', path: this.path(resource), data, params, error: err });
        }

        return throwError(this.handleError(err, resource, 'PUT', data, options));
      }));
  }

  post<T>(resource: string, data: any, parameters?: { [index: string]: string }, options?: ApiOptions): Observable<ApiResponse<T>> {
    const params = this.makeParams(parameters);
    return this.http.post<ApiResponse<T>>(this.path(resource), data, params).pipe(
      tap(() => this.handleServeStatus(false)),
      tap(res => this.setServerTime(res?.stime ?? '')),
      tap((value) => {
        if (options && options.invalidateOnSuccess) {
          this.cache.invalidateGroups(options.invalidateOnSuccess);
        }

      }),
      take(1),
      catchError((err: HttpErrorResponse) => {
        if (!environment.production) {
          console.error({ place: 'API', method: 'POST', path: this.path(resource), data, params, error: err });
        }
        return throwError(this.handleError(err, resource, 'POST', data, options));
      }));
  }

  delete<T = any>(resource: string, parameters?: { [index: string]: string }, options?: ApiOptions): Observable<ApiResponse<T>> {
    const params = this.makeParams(parameters);
    return this.http.delete<ApiResponse<any>>(this.path(resource), params).pipe(
      tap(() => this.handleServeStatus(false)),
      tap(res => this.setServerTime(res?.stime ?? '')),
      tap((value) => {
        if (options && options.invalidateOnSuccess) {
          this.cache.invalidateGroups(options.invalidateOnSuccess);
        }
      }),
      take(1),
      catchError(err => {
        if (!environment.production) {
          console.error({ place: 'API', method: 'DELETE', path: this.path(resource), params, error: err });
        }

        return throwError(this.handleError(err, resource, 'DELETE', null, options));
      }));
  }


  patch<T>(resource: string, data: any, parameters?: { [index: string]: string }, options?: ApiOptions): Observable<ApiResponse<T>> {
    const params = this.makeParams(parameters);

    return this.http.patch<ApiResponse<T>>(this.path(resource), data, params).pipe(
      tap(() => this.handleServeStatus(false)),
      tap(res => this.setServerTime(res?.stime ?? '')),
      tap((value) => {
        if (options && options.invalidateOnSuccess) {
          this.cache.invalidateGroups(options.invalidateOnSuccess);
        }
      }),
      take(1),
      catchError(err => {
        if (!environment.production) {
          console.error({ place: 'API', method: 'PATCH', path: this.path(resource), params, error: err });
        }
        return throwError(this.handleError(err, resource, 'PATCH', data, options));
      }));
  }

  invalidateCacheGroups(groups: CacheGroup | CacheGroup[]): Promise<void> {
    return this.cache.invalidateGroups(groups);
  }

  invalidatePerformanceCache(): Promise<void> {
    return this.cache.invalidateAll();
  }

  getLastCached<T = any>(endpoint: string) {
    return this.cache.getLastCache<T>(endpoint)
  }

  private handleServeStatus(hasConnection: boolean, error?: ApiError, options?: ApiOptions): void {

    if (hasConnection && (!this.hasConnection || this.hasConnection == null)) {

      this.store.dispatch(new Action_Api_Online());
    }
    if (!hasConnection && (this.hasConnection || this.hasConnection == null)) {

      this.store.dispatch(new Action_Api_Offline(error));
    }

  }

  toParamsObject(key: string, options: any): { [index: string]: string } {
    const _p = {};
    _p[key] = JSON.stringify(options);
    return _p;
  }

  private handleError(
    httpError: HttpErrorResponse, endpoint: string, method: string, payload?: any, options?: ApiOptions
  ): ApiResponseError {
    console.log(httpError);
    const error = httpError.error;
    // const isProduction = environment.environment == 'production';
    this.util.loader.close();

    let hasConnection = httpError.status !== 0 && httpError.status != 504;
    if ((httpError as any).code == 'auth/network-request-failed') {
      hasConnection = false;
    }

    const serverError = new ServerResponseError(httpError.error);


    this.handleServeStatus(hasConnection, {
      endpoint,
      method,
      error: httpError,
      message: 'Sem conexão com o servidor'
    });

    if (this.showError(httpError, options)) {

      if (httpError.status === 0) {
        return new ApiResponseError({
          code: 0,
          errorMessage: 'Servidor indisponível',
          loaderClosed: true,
          messageDisplayed: true,
          track: '',
          extra: httpError.error.extraInfo,
          apiResponse: serverError
        });
      }


      if (httpError.status >= 500 && httpError.status <= 599) {
        if (error.extraInfo && error.extraInfo.silent) {
          return new ApiResponseError({
            code: httpError.status,
            errorMessage: httpError.message,
            loaderClosed: true,
            messageDisplayed: true,
            track: '',
            extra: httpError.error.extraInfo,
            apiResponse: serverError
          });
        } else {

          if (httpError.status === 500) {
            if (this.showError(httpError, options)) {
              this.util.toast.error(`Não foi possível efetuar esta requisição. Tente mais tarde.`, {
                timer: 5
              });
            }
            return new ApiResponseError({
              code: httpError.status,
              errorMessage: httpError.message,
              loaderClosed: true,
              messageDisplayed: true,
              track: '',
              extra: httpError.error.extraInfo,
              apiResponse: serverError
            });
          }

          if (httpError.status === 503) {
            if (this.showError(httpError, options)) {
              this.util.toast.error(`Parece que estamos atualizando algumas coisas em nossos servidores.
            <br/>Nosso time está constantemente trabalhando para melhorar nosso sistema.
            <br>Por favor tenha paciencia e tente novamente em alguns minutos`, {
                timer: 20
              });
            }
            return new ApiResponseError({
              code: httpError.status,
              errorMessage: httpError.message,
              loaderClosed: true,
              messageDisplayed: true,
              track: '',
              extra: httpError.error.extraInfo,
              apiResponse: serverError
            });
          }

          if (httpError.status === 505) {
            if (this.showError(httpError, options)) {
              this.util.toast.error(
                `Parece que estamos atualizando algumas coisas em nossos servidores. <br/>
              Nosso time está constantemente trabalhando para melhorar nosso sistema.<br/>
              Por favor tenha paciencia e tente novamente em alguns minutos.
              `, {
                timer: 20
              });

            }
            const skipRedirect = []; // ['ping'];

            if (!skipRedirect.includes(endpoint)) {
              this.errorService.go({
                code: '505',
                message: httpError.error.error.message
              }, true);
            }


            return new ApiResponseError({
              code: httpError.status,
              errorMessage: httpError.message,
              loaderClosed: true,
              messageDisplayed: true,
              track: '',
              extra: httpError.error.extraInfo,
              apiResponse: serverError
            });
          }

          if (httpError.status === 504) {
            if (this.showError(httpError, options)) {
              this.util.toast.error(`Não foi possível conectar aos nossos servidores.<br>
            Por favor, tente novamente mais tarde`, {
                timer: 8
              });
            }
            return new ApiResponseError({
              code: httpError.status,
              errorMessage: httpError.message,
              loaderClosed: true,
              messageDisplayed: false,
              track: '',
              extra: httpError.error.extraInfo,
              apiResponse: serverError
            });
          }
        }

      }

      if (httpError.error?.info) {
        let m = httpError.error.info;
        if (httpError.error.tracker) {
          m += '<br>Se necessário você pode utilizar o código abaixo para rastrearmos este erro:<br>';
          m += httpError.error.tracker;
        }
        if (this.showError(httpError, options)) {
          this.util.toast.error(m, {
            timer: 20
          });
        }
        return new ApiResponseError({
          code: httpError.error.status,
          errorMessage: httpError.error.info,
          loaderClosed: true,
          messageDisplayed: true,
          track: httpError.error.tracker,
          extra: httpError.error.extraInfo,
          apiResponse: serverError
        });
      }


      if (httpError.status >= 400 && httpError.status <= 499) {

        if (httpError.status == 403) {
          if (error.extraInfo && error.extraInfo.type == 'not logged') {
            if (this.showError(httpError, options) && httpError.error?.error?.message) {

              this.util.dialog.inform(httpError.error?.error?.message, 'Acesso não permitido!').then(() => {

                signOut(this.af)
                  .then(async () => {
                    await this.router.navigate(['/'])
                    await this.router.navigate(['/signin']);

                  });
              });
            } else {
              signOut(this.af)
                .then(async () => {
                  await this.router.navigate(['/'])
                  await this.router.navigate(['/signin']);

                });
            }





            return new ApiResponseError({
              code: 403,
              errorMessage: `Não foi possível identificar o seu usuário em uma requisição, ]
              o que pode representar uma falha de segurança no sistema.<br>
              Eventualmente isto pode ser causado por uma baixa qualidade na conexão,
              mas por seguraça vamos precisar que você efetue o login novamente.`,
              loaderClosed: true,
              messageDisplayed: true,
              track: httpError.error.tracker,
              extra: httpError.error.extraInfo,
              apiResponse: serverError
            });
          }
        }

        let m = 'Falha na requisição. Tente novamente';

        if (typeof (error) == 'string') {
          m = error;
        } else {
          if (error && error.error && error.error.info) {
            m = error.error.info;
          } else if (error && error.error && error.error.message) {
            m = error.error.message;
          } else if (error.type) {
            m = this.knownTranslations(error.type, error.message ? error.message : m);
          } else if (error.code) {
            m = this.knownTranslations(error.code, error.message ? error.message : m);
          }
        }

        console.log(error);
        console.log(serverError);
        
        if (error.extraInfo && error.extraInfo.disconnect == true) {

          if (this.showError(httpError, options)) {
            this.util.dialog.inform(m, 'Acesso não permitido!').then(() => {
              signOut(this.af)
                .then(() => {
                  this.router.navigate(['/']);
                });
            });
          } else {
            signOut(this.af)
              .then(() => {
                this.router.navigate(['/']);
              });
          }
        } else {
          if (this.showError(httpError, options)) {
            this.util.toast.error(m);
          }
        }
        return new ApiResponseError({
          code: httpError.error.status,
          errorMessage: httpError.error.info,
          loaderClosed: true,
          messageDisplayed: true,
          track: httpError.error.tracker,
          extra: httpError.error.extraInfo,
          apiResponse: serverError
        });

      }

    }

    return new ApiResponseError({
      code: httpError.status,
      errorMessage: httpError.message,
      loaderClosed: true,
      messageDisplayed: false,
      track: '',
      extra: httpError.error.extraInfo,
      apiResponse: serverError
    });
  }

  private showError(error: HttpErrorResponse, apiOptions?: ApiOptions) {
    const serverError = new ServerResponseError(error.error);
    if (apiOptions) {
      if (apiOptions.silent) {
        return false;
      }

      if (apiOptions.silentIfCode) {
        if (apiOptions.silentIfCode.includes(error.error.status)) {
          return false;
        }
      }

      if (apiOptions.silentIfNamed) {
        for (let name of apiOptions.silentIfNamed) {
          if (serverError.isNamed(name)) {
            return false;
          }
        }
      }

    }

    return true;


  }

  private knownTranslations(code: string, defaultsTo: string): string {
    const known = {
      'entity.too.large': 'O tamanho da requisição é maior que o suportado por este servidor!'
    };

    const m = known[code];
    return m ? m : defaultsTo;


  }

  setServerTime(stime: string) {
    if (stime) {
      this.serverTime.set(stime);
    }
  }

}
